import { EditableText, SlotBlocks, defineBlock } from "eddev/blocks"
import { Chip } from "@components/atoms/Chip"
import * as Icons from "@components/atoms/Icons"
import { useSubtheme } from "@hooks/useSubtheme"
import { twMerge } from "tailwind-merge"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Link } from "eddev/routing"
import { Logo } from "@components/atoms/Logo"
import { Fragment, useEffect, useRef, useState } from "react"
import { kebabCase } from "@utils/kebabCase"
import { MobileAccordion } from "./driving-distractibility-tool"
import { useToolSubmission } from "@hooks/useToolSubmission"

export const meta: BlockMeta = {
  title: "Fatigue Detection Technology Assessment",
  category: "common",
  icon: "car",
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("rsrh/fatigue-detection-technology-assessment", (props) => {
  useSubtheme("grey")

  const [page, setPage] = useState(PAGE_HOME)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [deviceName, setDeviceName] = useState("")
  const [isExporting, setIsExporting] = useState(false)
  const summaryRef = useRef<HTMLDivElement>(null)
  const [activeTile, setActiveTile] = useState<string | null>(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [page])

  const toggleTile = (title: string) => setActiveTile((prev) => (prev === title ? null : title))
  const activeTechnologyTile = technologyTiles.find((tile) => tile.title === activeTile) ?? null

  const isQuestionPage = page >= PAGE_QUESTIONS_START && page < PAGE_SUMMARY
  const currentQuestion = isQuestionPage ? surveyQuestions[page - PAGE_QUESTIONS_START] : null

  const goNext = () => setPage((p) => Math.min(p + 1, PAGE_SUMMARY))
  const goBack = () => setPage((p) => Math.max(p - 1, PAGE_HOME))
  const restart = () => {
    setPage(PAGE_HOME)
    setAnswers({})
    setDeviceName("")
    setActiveTile(null)
    resetSubmission()
  }

  const exportSummary = async () => {
    if (!summaryRef.current || isExporting) return
    setIsExporting(true)
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")])
      const canvas = await html2canvas(summaryRef.current, { scale: 2, backgroundColor: "#ffffff" })

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const contentWidth = pageWidth - PDF_MARGIN_MM * 2
      const contentHeight = pageHeight - PDF_MARGIN_MM * 2

      // Slice the canvas into per-page chunks (rather than reusing one tall image at a shifting
      // offset) so the margin is respected at both the top and bottom of every page, not just the top.
      const pxPerMm = canvas.width / contentWidth
      const pageHeightPx = Math.floor(contentHeight * pxPerMm)

      let renderedHeightPx = 0
      let isFirstPage = true

      while (renderedHeightPx < canvas.height) {
        const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedHeightPx)

        const pageCanvas = document.createElement("canvas")
        pageCanvas.width = canvas.width
        pageCanvas.height = sliceHeightPx
        pageCanvas
          .getContext("2d")
          ?.drawImage(canvas, 0, renderedHeightPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx)

        if (!isFirstPage) pdf.addPage()
        pdf.addImage(
          pageCanvas.toDataURL("image/png"),
          "PNG",
          PDF_MARGIN_MM,
          PDF_MARGIN_MM,
          contentWidth,
          sliceHeightPx / pxPerMm,
        )

        renderedHeightPx += sliceHeightPx
        isFirstPage = false
      }

      const fileSlug = (deviceName || "fatigue-assessment").toLowerCase().trim().replace(/\s+/g, "-")
      pdf.save(`${fileSlug}-summary.pdf`)
    } finally {
      setIsExporting(false)
    }
  }
  const selectAnswer = (key: string, value: number) => setAnswers((prev) => ({ ...prev, [key]: value }))

  const paginationDots = (
    <div className="flex items-center gap-2 flex-wrap justify-center">
      {PAGINATION_PAGES.map((dotPage) => (
        <span
          key={dotPage}
          className={twMerge("w-2 h-2 rounded-full aaa-animate", page === dotPage ? "bg-black" : "bg-mid-grey/30")}
        />
      ))}
    </div>
  )

  const categoryAverages = Object.keys(survey)
    .map((category) => {
      const ratings = surveyQuestions
        .filter((q) => q.category === category)
        .map((q) => answers[q.key])
        .filter((value): value is number => typeof value === "number")
      return {
        category,
        average: ratings.length ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length : null,
      }
    })
    .filter((c): c is { category: string; average: number } => c.average !== null)

  const overallAverage = categoryAverages.length
    ? categoryAverages.reduce((sum, c) => sum + c.average, 0) / categoryAverages.length
    : null
  const overallLabel = overallAverage !== null ? RATING_LABELS[Math.round(overallAverage) - 1].toLowerCase() : null

  const [strongestCategory, weakestCategory] = [...categoryAverages].sort((a, b) => b.average - a.average)
  const hasSpread = strongestCategory && weakestCategory && strongestCategory.average > weakestCategory.average

  const { resetSubmission } = useToolSubmission(
    "fatigue-detection-technology-assessment",
    page === PAGE_SUMMARY,
    () => ({
      fields: {
        "Device Name": deviceName,
        "Technology Category": activeTile,
        "Overall Average": overallAverage,
        "Overall Label": overallLabel,
        "Device Efficacy Average": categoryAverages.find((c) => c.category === "Device Efficacy")?.average ?? null,
        "Device Features Average": categoryAverages.find((c) => c.category === "Device Features")?.average ?? null,
        "Impact On People And Organisations Average":
          categoryAverages.find((c) => c.category === "Impact on people and organisations")?.average ?? null,
        "Cost-Benefit Average": categoryAverages.find((c) => c.category === "Cost-Benefit")?.average ?? null,
        "Strongest Category": strongestCategory?.category ?? null,
        "Weakest Category": weakestCategory?.category ?? null,
        "Answers JSON": JSON.stringify(answers),
      },
    }),
  )

  const areaCountWord = NUMBER_WORDS[categoryAverages.length] ?? String(categoryAverages.length)

  const summaryText = overallLabel
    ? `You've completed your assessment of ${deviceName}. Across the ${areaCountWord} area${
        categoryAverages.length === 1 ? "" : "s"
      }, most of your ratings sat in the ${overallLabel} range${
        hasSpread
          ? `, with the strongest marks for ${strongestCategory.category.toLowerCase()} and a lower rating for ${weakestCategory.category.toLowerCase()}`
          : ""
      }. There's no overall score here and no pass or fail — a rating that works well for one operation may not suit another, so the picture below is meant to be read against what matters most to your fleet.`
    : `You've completed your assessment of ${deviceName}. There's no overall score here and no pass or fail — a rating that works well for one operation may not suit another, so the picture below is meant to be read against what matters most to your fleet.`

  const summaryBody = (
    <>
      <div className="w-full flex flex-col gap-6">
        <h2 className="!type-title-m !text-center">Assessment Summary &mdash; {deviceName}</h2>
        <div className="type-body-m">{summaryText}</div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <h3 className="!type-title-s !text-center">Your ratings at a glance</h3>

        <div className="grid grid-cols-8 w-full">
          <div className="col-span-3 py-2 border-b border-solid border-light-grey">
            <span className="type-label-caps text-dark-grey">Assessment category</span>
          </div>
          <div className="py-2 flex items-center justify-center border-b border-solid border-light-grey">
            <span className="type-label-caps text-dark-grey">1</span>
          </div>
          <div className="py-2 flex items-center justify-center border-b border-solid border-light-grey">
            <span className="type-label-caps text-dark-grey">2</span>
          </div>
          <div className="py-2 flex items-center justify-center border-b border-solid border-light-grey">
            <span className="type-label-caps text-dark-grey">3</span>
          </div>
          <div className="py-2 flex items-center justify-center border-b border-solid border-light-grey">
            <span className="type-label-caps text-dark-grey">4</span>
          </div>
          <div className="py-2 flex items-center justify-center border-b border-solid border-light-grey">
            <span className="type-label-caps text-dark-grey">5</span>
          </div>

          {Object.keys(survey).map((key, i) => {
            const categoryQuestions = surveyQuestions.filter((q) => q.category === key)
            const ratingCounts = [1, 2, 3, 4, 5].map(
              (rating) => categoryQuestions.filter((q) => answers[q.key] === rating).length,
            )
            return (
              <Fragment key={i}>
                <div className="col-span-3 py-2 border-b border-solid border-light-grey">
                  <span className="type-body-m">{key}</span>
                </div>
                {ratingCounts.map((count, ratingIndex) => (
                  <div
                    key={ratingIndex}
                    className="py-2 flex items-center justify-center gap-1 flex-wrap border-b border-solid border-light-grey"
                  >
                    {Array.from({ length: count }).map((_, dotIndex) => (
                      <span key={dotIndex} className="w-[6px] h-[6px] bg-yellow rounded-full" />
                    ))}
                  </div>
                ))}
              </Fragment>
            )
          })}
        </div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <h3 className="!type-title-s !text-center">Full results</h3>

        <div className="grid grid-cols-8 w-full">
          {Object.entries(survey).map(([key, data]) => (
            <div key={kebabCase(key)} className="col-span-8 grid grid-cols-8 w-full pb-6">
              <div className="col-span-8 py-2 border-b border-solid border-light-grey">
                <span className="type-label-caps text-dark-grey">{key}</span>
              </div>
              {data.map((qs, i) => (
                <Fragment key={i}>
                  {Object.entries(qs).map(([x, q]) => {
                    const answerValue = answers[`${key}__${x}`]
                    return (
                      <Fragment key={x}>
                        <div className="col-span-8 md:col-span-5 pt-6 pb-4 md:py-2 md:border-b border-solid border-light-grey">
                          <span className="type-body-s">{q}</span>
                        </div>
                        <div className="col-span-8 md:col-span-3 py-2 md:px-3 border-b border-solid border-light-grey flex items-center justify-start md:justify-end gap-3 pb-6 md:pb-0">
                          <div className="flex gap-[2px]">
                            {[1, 2, 3, 4, 5].map((segment) => (
                              <span
                                key={segment}
                                className={twMerge(
                                  "w-[32px] md:w-[16px] h-[8px] block",
                                  answerValue && segment <= answerValue ? "bg-yellow" : "bg-light-grey",
                                  segment === 1 && "rounded-l-full",
                                  segment === 5 && "rounded-r-full",
                                )}
                              />
                            ))}
                          </div>
                          <span className="type-body-s">{answerValue ?? "—"}</span>
                        </div>
                      </Fragment>
                    )
                  })}
                </Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  )

  return (
    <div
      className={twMerge(
        "px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] flex flex-col gap-4 md:gap-8 pt-6 pb-8 items-center mx-auto",
        env.admin && "bg-light-grey",
      )}
    >
      {page === PAGE_HOME && (
        <>
          <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
            <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-7">
              <div className="w-full flex flex-col gap-6 justify-start items-start">
                <Chip color="outline">Interactive Tool</Chip>
                <EditableText
                  as="h1"
                  store="title"
                  placeholder="Enter title..."
                  defaultValue="Fatigue Detection Technology Assessment"
                  className="type-title-xl"
                />
              </div>
              <div className="w-full flex flex-col gap-6 justify-start items-start">
                <EditableText as="p" store="content" placeholder="Enter content..." className="type-body-m" />
                <button className="!hidden md:!flex button-yellow" onClick={() => setPage(PAGE_INTRO_1)}>
                  <span>Get started (~15mins)</span>
                  <i>{Icons["ARROW_RIGHT"]}</i>
                </button>
              </div>
            </div>
            <div
              className={twMerge(
                "col-span-12 md:col-span-6 relative flex aspect-[10/7] bg-white md:bg-light-white",
                "[&>div]:!absolute [&>div]:!w-[calc(100%-(var(--spacing-4)*2))] [&>div]:!h-[calc(100%-(var(--spacing-4)*2))] [&>div]:!top-4 [&>div]:!left-4",
                "md:[&>div]:!w-full md:[&>div]:!h-full md:[&>div]:!top-0 md:[&>div]:!left-0",
                "[&_figure]:!absolute [&_figure]:!w-[calc(100%-(var(--spacing-4)*2))] [&_figure]:!h-[calc(100%-(var(--spacing-4)*2))] [&_figure]:!top-4 [&_figure]:!left-4 [&_figure]:rounded-md [&_figure]:overflow-hidden [&_figure]:!m-0",
                "md:[&_figure]:!w-full md:[&_figure]:!h-full md:[&_figure]:!top-0 md:[&_figure]:!left-0 md:[&_figure]:rounded-none",
                "[&_figure>div]:!absolute [&_figure>div]:!w-full [&_figure>div]:!h-full [&_figure>div]:!top-0 [&_figure>div]:!left-0",
                "[&_figure>img]:!absolute [&_figure>img]:!m-0 [&_figure>img]:!w-full [&_figure>img]:!h-full [&_figure>img]:!top-0 [&_figure>img]:!left-0 [&_figure>img]:!rounded-none [&_figure>img]:!object-cover",
              )}
            >
              <SlotBlocks
                id="image"
                allowedBlocks={["core/image"]}
                template={[["core/image", {}]]}
                templateLock="all"
              />
            </div>
          </div>

          <div className="md:hidden grid-auto !w-full">
            <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
              <button className="button-yellow w-full box-border" onClick={() => setPage(PAGE_INTRO_1)}>
                <span>Get started (~15mins)</span>
                <i>{Icons["ARROW_RIGHT"]}</i>
              </button>
            </div>
          </div>
        </>
      )}

      {page === PAGE_INTRO_1 && (
        <>
          <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
            <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-7">
              <span className="type-label-caps text-mid-grey">Welcome</span>

              <div className="flex flex-col gap-4">
                <EditableText as="p" store="intro_1_title" placeholder="Intro title..." className="type-title-s" />
                <EditableText as="p" store="intro_1_content" placeholder="Intro content..." className="type-body-m" />
              </div>

              <button className="!hidden md:!flex button-yellow" onClick={goNext}>
                <span>I'm ready, lets get started</span>
                <i>{Icons["ARROW_RIGHT"]}</i>
              </button>
            </div>
            <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
              <MobileAccordion title="What you'll need before you start">
                <SlotBlocks id="list" allowedBlocks={["core/list"]} template={[["core/list", {}]]} templateLock="all" />
              </MobileAccordion>
            </div>
          </div>

          <div className="md:hidden grid-auto !w-full">
            <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
              <button className="button-yellow w-full box-border" onClick={goNext}>
                <span>I'm ready, lets get started</span>
                <i>{Icons["ARROW_RIGHT"]}</i>
              </button>
            </div>
          </div>
        </>
      )}

      {page === PAGE_INTRO_2 && (
        <>
          <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
            <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-7">
              <span className="type-label-caps text-mid-grey">What are we assessing?</span>

              <div className="w-full flex flex-col gap-4">
                <h3 className="!type-title-s !text-center w-full">Overview of the current technologies</h3>

                <div className="grid grid-cols-1 md:grid-cols-5 w-full gap-3 md:gap-2 my-4">
                  {technologyTiles.map((tile) => (
                    <button
                      key={tile.title}
                      type="button"
                      onClick={() => toggleTile(tile.title)}
                      className={twMerge(
                        "flex flex-col justify-between items-center text-center rounded-sm bg-light-grey p-4 gap-4 aaa-animate",
                        activeTile === tile.title && "bg-yellow",
                      )}
                    >
                      <div className="relative flex items-center justify-center w-full h-[60px] md:h-0 overflow-hidden md:pt-[100%] md:[&_svg]:absolute md:[&_svg]:top-1/2 md:[&_svg]:left-1/2 md:[&_svg]:-translate-x-1/2 md:[&_svg]:-translate-y-1/2">
                        {tile.icon}
                      </div>
                      <span className="type-label-caps">{tile.title}</span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-col gap-2 w-full">
                  <label className="type-label-caps text-mid-grey">
                    What's the name of the technology/product you are assessing?
                  </label>
                  <input
                    type="text"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="e.g. Ocular Cam 2026"
                    className="w-full h-[48px] rounded-sm border border-mid-grey px-4 type-body-m"
                  />
                </div>
              </div>

              <div className="w-full hidden md:flex items-center justify-between">
                <button className="button-grey" onClick={goBack}>
                  <i>{Icons["ARROW_LEFT"]}</i>
                  <span>Back</span>
                </button>

                {paginationDots}

                <button
                  className="button-yellow disabled:button-disabled"
                  onClick={goNext}
                  disabled={!deviceName.trim() || !activeTile}
                >
                  <span>Next</span>
                  <i>{Icons["ARROW_RIGHT"]}</i>
                </button>
              </div>
            </div>
            <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
              {activeTechnologyTile ? (
                <div className="flex flex-col gap-4">
                  <span className="type-label-caps text-mid-grey">Benefits</span>
                  <SlotBlocks
                    id={`list_a_${kebabCase(activeTechnologyTile.title)}`}
                    allowedBlocks={["core/list"]}
                    template={[["core/list", {}]]}
                    templateLock="all"
                  />
                  <span className="type-label-caps text-mid-grey mt-4">Considerations</span>
                  <SlotBlocks
                    id={`list_b_${kebabCase(activeTechnologyTile.title)}`}
                    allowedBlocks={["core/list"]}
                    template={[["core/list", {}]]}
                    templateLock="all"
                  />
                </div>
              ) : (
                <MobileAccordion title="Overview">
                  <SlotBlocks
                    id="list_2"
                    allowedBlocks={["core/list"]}
                    template={[["core/list", {}]]}
                    templateLock="all"
                  />
                </MobileAccordion>
              )}
            </div>
          </div>

          <div className="md:hidden grid-auto !w-full">
            <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
              <button className="button-grey" onClick={goBack}>
                <i>{Icons["ARROW_LEFT"]}</i>
                <span>Back</span>
              </button>

              <button
                className="button-yellow disabled:button-disabled"
                onClick={goNext}
                disabled={!deviceName.trim() || !activeTile}
              >
                <span>Next</span>
                <i>{Icons["ARROW_RIGHT"]}</i>
              </button>
            </div>
          </div>
        </>
      )}

      {isQuestionPage && currentQuestion && (
        <>
          <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
            <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-7">
              <span className="type-label-caps text-mid-grey">
                {currentQuestion.label} &mdash; {page - PAGE_QUESTIONS_START + 1} of {surveyQuestions.length}
              </span>

              <div className="w-full flex flex-col items-center gap-4">
                <h3 className="!type-title-s !text-center w-full !max-w-[480px] mx-auto">{currentQuestion.prompt}</h3>

                <div className="flex flex-wrap justify-center md:grid md:grid-cols-5 w-full gap-3 md:gap-2 my-4">
                  {RATING_LABELS.map((label, index) => {
                    const value = index + 1
                    return (
                      <button
                        key={value}
                        onClick={() => selectAnswer(currentQuestion.key, value)}
                        className={twMerge(
                          "w-[calc((100%-(var(--spacing-3)*2))*(1/3))] md:w-full flex flex-col justify-between items-center text-center rounded-sm p-4 gap-4 box-border",
                          answers[currentQuestion.key] === value ? "bg-yellow" : "bg-light-grey",
                        )}
                      >
                        <div className="relative w-full h-0 overflow-hidden pt-[75%] [&_span]:absolute [&_span]:top-1/2 [&_span]:left-1/2 [&_span]:-translate-x-1/2 [&_span]:-translate-y-1/2">
                          <span className="type-title-l">{value}</span>
                        </div>

                        <span className="type-label-caps">{label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="w-full hidden md:flex gap-4 items-center justify-between">
                <button className="button-grey" onClick={goBack}>
                  <i>{Icons["ARROW_LEFT"]}</i>
                  <span>Back</span>
                </button>
                {paginationDots}
                <button
                  className="button-yellow disabled:button-disabled"
                  onClick={goNext}
                  disabled={!answers[currentQuestion.key]}
                >
                  <span>{page === PAGE_SUMMARY - 1 ? "See summary" : "Next"}</span>
                  <i>{Icons["ARROW_RIGHT"]}</i>
                </button>
              </div>
            </div>

            <div className="col-span-12 md:col-span-6 relative flex flex-col gap-6 justify-center md:aspect-[10/7] bg-light-white p-8">
              <MobileAccordion title="Key considerations &amp; questions">
                <EditableText
                  store={`list_text_${page - PAGE_QUESTIONS_START + 1}`}
                  className="type-body-m mb-4"
                  placeholder="Enter text..."
                />
              </MobileAccordion>

              <MobileAccordion title="To rate highly">
                <div className="[&_ul]:mt-0 [&_ul>li]:mt-0">
                  <SlotBlocks
                    id={`list_question_${page - PAGE_QUESTIONS_START + 1}`}
                    allowedBlocks={["core/list"]}
                    template={[["core/list", {}]]}
                    templateLock="all"
                  />
                </div>
              </MobileAccordion>
            </div>
          </div>

          <div className="md:hidden grid-auto !w-full">
            <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
              <button className="button-grey" onClick={goBack}>
                <i>{Icons["ARROW_LEFT"]}</i>
                <span>Back</span>
              </button>

              <button
                className="button-yellow disabled:button-disabled"
                onClick={goNext}
                disabled={!answers[currentQuestion.key]}
              >
                <span>{page === PAGE_SUMMARY - 1 ? "See summary" : "Next"}</span>
                <i>{Icons["ARROW_RIGHT"]}</i>
              </button>
            </div>
          </div>
        </>
      )}

      {page === PAGE_SUMMARY && (
        <div className="grid-auto !w-full gap-6 p-4 md:p-8 rounded-md overflow-hidden bg-white">
          <div className="col-span-12 flex flex-col">
            <span className="type-label-caps text-mid-grey">Results</span>
          </div>
          <div className="col-span-12 flex flex-col items-center">
            <div className="flex flex-col items-center gap-10 w-full max-w-[750px] bg-white md:p-6">{summaryBody}</div>

            <div
              ref={summaryRef}
              aria-hidden="true"
              className="flex flex-col items-center gap-10 w-full max-w-[750px] bg-white p-6 fixed top-0 -left-[9999px] pointer-events-none"
            >
              <div className="w-full flex justify-start">
                <Logo />
              </div>

              {summaryBody}
            </div>

            <div className="w-full flex flex-col md:flex-row gap-4 items-center px-4 md:px-0 justify-center mb-4 md:mb-6 max-w-[750px]">
              <button className="button-grey w-full md:w-auto" onClick={exportSummary} disabled={isExporting}>
                <span>{isExporting ? "Preparing PDF..." : "Export Summary"}</span>
                <i>{Icons["DOWNLOAD"]}</i>
              </button>

              <button className="button-grey w-full md:w-auto" onClick={restart}>
                <span>Assess Another Device</span>
                <i>{Icons["ADD"]}</i>
              </button>

              {props.implementationGuide?.url && (
                <Link className="button-black w-full md:w-auto" href={props.implementationGuide.url} target="_blank">
                  <span>Implementation Guide</span>
                  <i>{Icons["LINK"]}</i>
                </Link>
              )}
            </div>

            <div className="w-full flex items-center justify-between">
              <button className="button-grey w-full md:w-auto" onClick={goBack}>
                <i>{Icons["ARROW_LEFT"]}</i>
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid-auto !w-full gap-6 mt-6 md:mt-0">
        <div className="col-span-12 md:col-span-4 flex bg-white rounded-md overflow-hidden hover:bg-yellow aaa-animate">
          {props.document && (
            <Link
              href={props.document.uri}
              target="_blank"
              className="w-full flex rounded-md gap-4 p-4 relative overflow-hidden aaa-animate"
            >
              <div className="flex flex-grow flex-col gap-4 justify-between">
                <div className="flex flex-grow flex-col gap-2">
                  <span className="type-label-caps text-mid-grey">Print Version</span>
                  <h4 className="!type-title-xs">{props.document.title}</h4>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {props?.document?.documentTopics?.nodes
                    ?.concat(props?.document?.documentTypes?.nodes ?? [])
                    .map((category, i) => (
                      <Chip key={i} color="outline">
                        {category.name}
                      </Chip>
                    ))}
                </div>
              </div>

              {props.document?.featuredImage?.node ? (
                <ResponsiveImage
                  {...props.document.featuredImage.node}
                  aspect={0.71}
                  className="rounded-sm w-1/4 overflow-hidden"
                />
              ) : (
                <Logo />
              )}
            </Link>
          )}
        </div>
        <div className="col-span-12 md:col-span-4 flex bg-white rounded-md p-4 flex-col gap-4">
          <EditableText as="span" store="title_2" className="type-label-caps text-mid-grey" placeholder="Text..." />
          <EditableText as="span" store="text_2" className="type-body-s" placeholder="Text..." />
        </div>
        <div className="col-span-12 md:col-span-4 flex bg-white rounded-md p-4 flex-col gap-4">
          <EditableText as="span" store="title_3" className="type-label-caps text-mid-grey" placeholder="Text..." />
          <div className="grid grid-cols-3 w-full gap-4">
            {props.researchPartners?.map((node, i) => (
              <ResponsiveImage
                {...node?.logo}
                key={i}
                objectFit="contain"
                className="flex w-full items-center justify-center"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})

const survey = {
  "Device Efficacy": [
    {
      "Quality of validation studies":
        "Has the device been independently validated, and can the vendor point to the validation studies? ",
    },
    {
      "False negatives": "How often does the device fail to detect real fatigue?",
    },
    {
      "False positives": "How frequently are alarms triggered when drivers are not experiencing real fatigue?",
    },
    {
      "Detection latency": "Does the device detect early-stage fatigue?",
    },
    {
      Reliability:
        "Does the device work consistently across a variety of circumstances, environments, and locations? (Are there specific requirements for optimal functioning, such as Wi-Fi connectivity or specific road environments?)",
    },
    {
      "Notification time for FDT failure":
        "If the device stops working effectively, will it inform the driver and/or management team?",
    },
  ],
  "Device Features": [
    {
      Availability: "Is the device readily available for purchase or implementation?",
    },
    {
      Compatibility:
        "Can the device be easily installed into the vehicle and easily used? Is the device compatible across a variety of vehicles (e.g. if the fleet has different types of vehicles or models)?",
    },
    {
      "Intrusiveness and discomfort":
        "Would drivers find the device to be significantly intrusive? Will it impede driver comfort, vision, or ability to undertake the work tasks?",
    },
    {
      "Data visibility":
        "High levels of data visibility enable organisation-level fatigue management, as well as maintenance and troubleshooting. Is the data available to drivers, managers, and the vendor?",
    },
    {
      "Data security": "How is the data stored securely?",
    },
  ],
  "Impact on people and organisations": [
    {
      "Training load":
        "What training will drivers and management need to use the device and to interpret and respond to alarms? Are there known organisational barriers to implementation?",
    },
    {
      "Driver workload":
        "To what extent will the FDT divert drivers from their primary tasks (e.g. especially driving)? Are there fitting, adjustment, and calibration requirements?",
    },
    {
      "Administrative workload":
        "What organisational resource allocation is needed beyond receiving and responding to alerts? (Does the device require your organisation to monitor and respond to the alarms/videos/ information captured?)",
    },
    {
      "Vendor support": "Does the vendor offer ongoing support for implementation and utilisation?",
    },
  ],
  "Cost-Benefit": [
    {
      "Cost-benefit": "How much does the device cost? Does its efficacy and expected benefits justify the cost?",
    },
  ],
}

const technologyTiles: { icon: JSX.Element; title: string }[] = [
  {
    icon: (
      <svg width="39" height="27" viewBox="0 0 39 27" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 13.5C12 9.35786 15.3579 6 19.5 6C23.6421 6 27 9.35786 27 13.5C27 17.6421 23.6421 21 19.5 21C15.3579 21 12 17.6421 12 13.5ZM19.5 9C17.0147 9 15 11.0147 15 13.5C15 15.9853 17.0147 18 19.5 18C21.9853 18 24 15.9853 24 13.5C24 11.0147 21.9853 9 19.5 9Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.14685 10.7927C3.30862 12.0006 3 12.9454 3 13.5C3 14.0546 3.30862 14.9994 4.14685 16.2073C4.95714 17.3749 6.16155 18.6406 7.68534 19.8101C10.7396 22.1542 14.9269 24 19.5 24C24.0731 24 28.2604 22.1542 31.3147 19.8101C32.8384 18.6406 34.0429 17.3749 34.8531 16.2073C35.6914 14.9994 36 14.0546 36 13.5C36 12.9454 35.6914 12.0006 34.8531 10.7927C34.0429 9.62511 32.8384 8.35944 31.3147 7.18993C28.2604 4.84584 24.0731 3 19.5 3C14.9269 3 10.7396 4.84584 7.68534 7.18993C6.16155 8.35944 4.95714 9.62511 4.14685 10.7927ZM5.85881 4.81007C9.31931 2.15416 14.132 0 19.5 0C24.868 0 29.6807 2.15416 33.1412 4.81007C34.8748 6.14056 36.3064 7.62489 37.3178 9.08229C38.3013 10.4994 39 12.0546 39 13.5C39 14.9454 38.3013 16.5006 37.3178 17.9177C36.3064 19.3751 34.8748 20.8594 33.1412 22.1899C29.6807 24.8458 24.868 27 19.5 27C14.132 27 9.31931 24.8458 5.85881 22.1899C4.12525 20.8594 2.69362 19.3751 1.68221 17.9177C0.698742 16.5006 0 14.9454 0 13.5C0 12.0546 0.698742 10.4994 1.68221 9.08229C2.69362 7.62489 4.12525 6.14056 5.85881 4.81007Z"
          fill="#222222"
        />
      </svg>
    ),
    title: "Ocular",
  },
  {
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.4793 9.75185C16.3649 9.06978 15.799 8.55435 15.1092 8.50399C14.4195 8.45362 13.7847 8.88138 13.5724 9.53959L9.39253 22.5H4C3.17157 22.5 2.5 23.1716 2.5 24C2.5 24.8284 3.17157 25.5 4 25.5H10.4848C11.1359 25.5 11.7126 25.08 11.9124 24.4604L14.5387 16.3171L18.2176 38.2482C18.3336 38.9393 18.9127 39.4581 19.6124 39.4976C20.3121 39.5371 20.9459 39.0869 21.139 38.4131L25.998 21.4531L28.5577 30.4121C28.7227 30.9894 29.216 31.4128 29.8118 31.4881C30.4075 31.5635 30.9907 31.2763 31.2942 30.7582L34.3748 25.5H36.707C37.36 27.8085 39.4824 29.5 42 29.5C45.0376 29.5 47.5 27.0376 47.5 24C47.5 20.9624 45.0376 18.5 42 18.5C39.4824 18.5 37.36 20.1915 36.707 22.5H33.5152C32.9827 22.5 32.4901 22.7823 32.2209 23.2418L30.4787 26.2155L27.4423 15.5879C27.2584 14.9442 26.6701 14.5002 26.0005 14.5C25.331 14.4998 24.7424 14.9432 24.558 15.5869L20.08 31.2167L16.4793 9.75185ZM39.5 24C39.5 22.6193 40.6193 21.5 42 21.5C43.3807 21.5 44.5 22.6193 44.5 24C44.5 25.3807 43.3807 26.5 42 26.5C40.6193 26.5 39.5 25.3807 39.5 24Z"
          fill="#222222"
        />
      </svg>
    ),
    title: "Heart Rate",
  },
  {
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M33.293 6.67775C34.0235 6.28713 34.9316 6.56248 35.3223 7.29298L35.3242 7.29494L35.3281 7.30275C35.3304 7.30697 35.3328 7.31228 35.3359 7.31837C35.3424 7.33074 35.3511 7.34707 35.3613 7.3672C35.3822 7.4083 35.4117 7.46721 35.4473 7.54103C35.5183 7.68855 35.6177 7.90206 35.7402 8.18556C35.986 8.75416 36.3265 9.60412 36.7227 10.7656C37.5149 13.0886 38.5319 16.6589 39.4746 21.7246C40.4165 26.7864 40.9229 31.3396 41.1934 34.627C41.3286 36.2712 41.4044 37.6014 41.4473 38.5235C41.4687 38.9845 41.4826 39.3443 41.4902 39.5899C41.4941 39.7124 41.4965 39.8066 41.498 39.8711C41.4988 39.9031 41.4996 39.9282 41.5 39.9453V39.9727C41.514 40.8007 40.8535 41.4856 40.0254 41.5C39.1974 41.514 38.5144 40.8535 38.5 40.0254V40.0078C38.4997 39.9941 38.4987 39.9722 38.498 39.9434C38.4967 39.8858 38.4938 39.7987 38.4902 39.6836C38.483 39.4527 38.4718 39.1089 38.4512 38.6641C38.4098 37.7737 38.3352 36.4784 38.2031 34.8731C37.9388 31.6607 37.4442 27.2133 36.5254 22.2754C35.6073 17.3419 34.6253 13.9113 33.8828 11.7344C33.5117 10.6462 33.1998 9.87089 32.9863 9.37697C32.8796 9.13001 32.7972 8.95199 32.7441 8.84181C32.7178 8.78717 32.6988 8.74887 32.6875 8.72658C32.6818 8.71539 32.6758 8.70509 32.6758 8.70509L32.6777 8.70705C32.2871 7.97653 32.5625 7.06841 33.293 6.67775Z"
          fill="black"
        />
        <path
          d="M6.5 40V39.8965C6.50052 39.8314 6.50188 39.7348 6.50391 39.6113C6.50795 39.3647 6.51534 39.0046 6.53125 38.543C6.56315 37.6179 6.62712 36.2831 6.75391 34.6348C7.00733 31.3403 7.51476 26.7778 8.5293 21.7051C9.54055 16.649 10.5551 13.0885 11.3262 10.7754C11.7116 9.61918 12.0373 8.77504 12.2695 8.21095C12.3857 7.92891 12.4788 7.71586 12.5449 7.57033C12.5777 7.49814 12.604 7.44233 12.623 7.40236C12.6326 7.38231 12.6406 7.36559 12.6465 7.35353L12.6582 7.33009L12.7344 7.19533C13.1413 6.55364 13.9755 6.31152 14.6699 6.65822C15.4106 7.02857 15.7117 7.92912 15.3418 8.66994C15.34 8.67363 15.3356 8.68169 15.3301 8.69337C15.3189 8.71683 15.3007 8.75683 15.2754 8.81251C15.2244 8.92473 15.1453 9.103 15.043 9.35158C14.8377 9.84999 14.5383 10.6313 14.1738 11.7246C13.4449 13.9114 12.4594 17.3514 11.4707 22.2949C10.4854 27.2219 9.99266 31.6599 9.74609 34.8652C9.62294 36.4663 9.56186 37.7572 9.53125 38.6445C9.51595 39.0882 9.50767 39.4324 9.50391 39.6621C9.50203 39.7766 9.50045 39.8631 9.5 39.9199V40C9.5 40.8284 8.82843 41.5 8 41.5C7.17157 41.5 6.5 40.8284 6.5 40Z"
          fill="black"
        />
        <path
          d="M22.5 10V8.00001C22.5 7.17159 23.1716 6.50001 24 6.50001C24.8284 6.50001 25.5 7.17159 25.5 8.00001V10C25.5 10.8284 24.8284 11.5 24 11.5C23.1716 11.5 22.5 10.8284 22.5 10Z"
          fill="black"
        />
        <path
          d="M22.5 23V17C22.5 16.1716 23.1716 15.5 24 15.5C24.8284 15.5 25.5 16.1716 25.5 17V23C25.5 23.8284 24.8284 24.5 24 24.5C23.1716 24.5 22.5 23.8284 22.5 23Z"
          fill="black"
        />
        <path
          d="M22.5 40V31C22.5 30.1716 23.1716 29.5 24 29.5C24.8284 29.5 25.5 30.1716 25.5 31V40C25.5 40.8284 24.8284 41.5 24 41.5C23.1716 41.5 22.5 40.8284 22.5 40Z"
          fill="black"
        />
      </svg>
    ),
    title: "Lane Keeping / Deviation",
  },
  {
    icon: (
      <svg width="43" height="43" viewBox="0 0 43 43" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M21.0611 3.21947L16.6355 7.79847C16.1644 8.28584 15.5157 8.56107 14.8378 8.56107H8.56107V14.8378C8.56107 15.5157 8.28584 16.1644 7.79847 16.6355L3.21947 21.0611L7.79847 25.4867C8.28585 25.9577 8.56107 26.6065 8.56107 27.2843V33.5611H14.8378C15.5157 33.5611 16.1644 33.8363 16.6355 34.3237L21.0611 38.9027L25.4867 34.3237C25.9577 33.8363 26.6065 33.5611 27.2843 33.5611H33.5611V27.2843C33.5611 26.6065 33.8363 25.9577 34.3237 25.4867L38.9027 21.0611L34.3237 16.6355C33.8363 16.1644 33.5611 15.5157 33.5611 14.8379V8.56107H27.2843C26.6065 8.56107 25.9577 8.28585 25.4867 7.79847L21.0611 3.21947ZM19.2634 0.762601C20.2462 -0.254201 21.8759 -0.2542 22.8587 0.762601L27.4964 5.56107H34.0611C35.4418 5.56107 36.5611 6.68036 36.5611 8.06107V14.6257L41.3595 19.2634C42.3763 20.2462 42.3763 21.876 41.3595 22.8587L36.5611 27.4964V34.0611C36.5611 35.4418 35.4418 36.5611 34.0611 36.5611H27.4964L22.8587 41.3595C21.8759 42.3763 20.2462 42.3763 19.2634 41.3595L14.6257 36.5611H8.06107C6.68036 36.5611 5.56107 35.4418 5.56107 34.0611V27.4964L0.7626 22.8587C-0.254199 21.876 -0.254201 20.2462 0.7626 19.2634L5.56107 14.6257V8.06107C5.56107 6.68035 6.68036 5.56107 8.06107 5.56107H14.6257L19.2634 0.762601Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.5611 21.0611C11.5611 15.8144 15.8144 11.5611 21.0611 11.5611C26.3078 11.5611 30.5611 15.8144 30.5611 21.0611C30.5611 26.3078 26.3078 30.5611 21.0611 30.5611C15.8144 30.5611 11.5611 26.3078 11.5611 21.0611ZM21.0611 14.5611C17.4712 14.5611 14.5611 17.4712 14.5611 21.0611C14.5611 24.6509 17.4712 27.5611 21.0611 27.5611C24.6509 27.5611 27.5611 24.6509 27.5611 21.0611C27.5611 17.4712 24.6509 14.5611 21.0611 14.5611Z"
          fill="#222222"
        />
      </svg>
    ),
    title: "Pre-drive fitness",
  },
  {
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M8.5 42V29.5H6C5.17157 29.5 4.5 28.8284 4.5 28C4.5 27.1716 5.17157 26.5 6 26.5H14C14.8284 26.5 15.5 27.1716 15.5 28C15.5 28.8284 14.8284 29.5 14 29.5H11.5V42C11.5 42.8284 10.8284 43.5 10 43.5C9.17157 43.5 8.5 42.8284 8.5 42ZM22.5 42V24C22.5 23.1716 23.1716 22.5 24 22.5C24.8284 22.5 25.5 23.1716 25.5 24V42C25.5 42.8284 24.8284 43.5 24 43.5C23.1716 43.5 22.5 42.8284 22.5 42ZM36.5 42V33.5H34C33.1716 33.5 32.5 32.8284 32.5 32C32.5 31.1716 33.1716 30.5 34 30.5H42C42.8284 30.5 43.5 31.1716 43.5 32C43.5 32.8284 42.8284 33.5 42 33.5H39.5V42C39.5 42.8284 38.8284 43.5 38 43.5C37.1716 43.5 36.5 42.8284 36.5 42ZM36.5 24V6C36.5 5.17157 37.1716 4.5 38 4.5C38.8284 4.5 39.5 5.17157 39.5 6V24C39.5 24.8284 38.8284 25.5 38 25.5C37.1716 25.5 36.5 24.8284 36.5 24ZM8.5 20V6C8.5 5.17157 9.17157 4.5 10 4.5C10.8284 4.5 11.5 5.17157 11.5 6V20C11.5 20.8284 10.8284 21.5 10 21.5C9.17157 21.5 8.5 20.8284 8.5 20ZM22.5 6C22.5 5.17157 23.1716 4.5 24 4.5C24.8284 4.5 25.5 5.17157 25.5 6V14.5H28C28.8284 14.5 29.5 15.1716 29.5 16C29.5 16.8284 28.8284 17.5 28 17.5H20C19.1716 17.5 18.5 16.8284 18.5 16C18.5 15.1716 19.1716 14.5 20 14.5H22.5V6Z"
          fill="black"
        />
      </svg>
    ),
    title: "Native systems",
  },
]

const surveyQuestions = Object.entries(survey).flatMap(([category, items]) =>
  items.map((item) => {
    const [label, prompt] = Object.entries(item)[0]
    return { key: `${category}__${label}`, category, label, prompt }
  }),
)

const RATING_LABELS = ["Very dissatisfied", "Dissatisfied", "Neither", "Satisfied", "Very satisfied"]

const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"]

// 1 inch — the standard Word document default page margin.
const PDF_MARGIN_MM = 25.4

const PAGE_HOME = 0
const PAGE_INTRO_1 = 1
const PAGE_INTRO_2 = 2
const PAGE_QUESTIONS_START = 3
const PAGE_SUMMARY = PAGE_QUESTIONS_START + surveyQuestions.length

// Every page that shows the dot indicator: intro 2 through the last question (excludes home, intro 1, summary).
const PAGINATION_PAGES = Array.from({ length: PAGE_SUMMARY - PAGE_INTRO_2 }, (_, i) => PAGE_INTRO_2 + i)
